import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const useMasterList = ({ resource, searchText = "", label }) => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lifecycleStatus, setLifecycleStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState({});
  const [lifecycleTarget, setLifecycleTarget] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchRows = useCallback(() => {
    setLoading(true);

    resource
      .list({
        page: pageNumber + 1,
        perPage: rowsPerPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(lifecycleStatus ? { lifecycleStatus } : {}),
      })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows(list);

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || `Could not load ${label}.`
        );
      })
      .finally(() => setLoading(false));
  }, [resource, pageNumber, rowsPerPage, debouncedSearch, lifecycleStatus, label]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setActiveRecord({});
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setActiveRecord(record);
    setModalOpen(true);
  };

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveRecord({});
    if (reason !== "close") fetchRows();
  };

  const submitLifecycle = async ({ lifecycleStatus: next, retiredReason }) => {
    if (!lifecycleTarget?._id) return;

    setBusy(true);
    try {
      await resource.setLifecycle(lifecycleTarget._id, {
        lifecycleStatus: next,
        ...(next === "RETIRED" ? { retiredReason } : {}),
      });
      toast.success(
        `${label} ${next === "RETIRED" ? "retired" : "reactivated"} successfully`
      );
      setLifecycleTarget(null);
      fetchRows();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        `Could not update the status of this ${label.toLowerCase()}.`
      );
    } finally {
      setBusy(false);
    }
  };

  return {
    rows,
    count,
    pageNumber,
    rowsPerPage,
    lifecycleStatus,
    setLifecycleStatus,
    loading,
    busy,
    modalOpen,
    activeRecord,
    lifecycleTarget,
    setLifecycleTarget,
    handleChangePage: (event, newPage) => setPageNumber(newPage),
    handleChangeRowsPerPage: (event) => {
      setRowsPerPage(Number(event.target.value));
      setPageNumber(0);
    },
    openCreate,
    openEdit,
    handleModalClose,
    submitLifecycle,
    refresh: fetchRows,
  };
};

export default useMasterList;
